import { defineAsyncComponent } from '../../lib/utils/vue3compat/vue.js?vue3';
import { GlButton, GlTooltipDirective } from '../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import { mapActions, mapState } from '../../../../../node_modules/pinia/dist/pinia.mjs?vue3';
import { __ } from '~/locale';
import Shortcuts from '../../behaviors/shortcuts/shortcuts.js?vue3';
import { keyboardShortcutsDisabled } from '~/behaviors/shortcuts/shortcuts_disabled';
import { keysFor, TOGGLE_FILE_TREE_BROWSER_VISIBILITY, START_SEARCH_PROJECT_FILE } from '~/behaviors/shortcuts/keybindings';
import { sanitize } from '~/lib/dompurify';
import { FIND_FILE_BUTTON_CLICK, REF_SELECTOR_CLICK } from '~/tracking/constants';
import { visitUrl, joinPaths, webIDEUrl } from '~/lib/utils/url_utility';
import { getIdFromGraphQLId } from '~/graphql_shared/utils';
import FileTreeBrowserToggle from '../file_tree_browser/components/file_tree_browser_toggle.vue?vue3';
import { generateRefDestinationPath } from '~/repository/utils/ref_switcher_utils';
import RefSelector from '../../vue_shared/components/ref/components/ref_selector.vue?vue3';
import Breadcrumbs from './header_area/breadcrumbs.vue?vue3';
import BlobControls from './header_area/blob_controls.vue?vue3';
import RepositoryOverflowMenu from './header_area/repository_overflow_menu.vue?vue3';
import AddToTree from './header_area/add_to_tree.vue?vue3';
import FileIcon from '../../vue_shared/components/file_icon.vue?vue3';
import { useFileTreeBrowserVisibility } from '../stores/file_tree_browser_visibility.js?vue3';
import { Mousetrap } from '~/lib/mousetrap';
import { EVENT_COLLAPSE_FILE_TREE_BROWSER_ON_REPOSITORY_PAGE, EVENT_EXPAND_FILE_TREE_BROWSER_ON_REPOSITORY_PAGE } from '~/repository/constants';
import { InternalEvents } from '../../tracking/index.js?vue3';
import { glListenersMixin } from '~/lib/utils/vue3compat/gl_listeners_mixin';
export default {
    name: 'RepositoryHeaderArea',
    i18n: {
        compare: __('Compare'),
        findFile: __('Find file')
    },
    components: {
        GlButton,
        FileIcon,
        RefSelector,
        Breadcrumbs,
        RepositoryOverflowMenu,
        BlobControls,
        CompactCodeDropdown: defineAsyncComponent(()=>import('../../../../../ee/app/assets/javascripts/repository/components/code_dropdown/compact_code_dropdown.vue?vue3')),
        AddToTree,
        WebIdeLink: defineAsyncComponent(()=>import('../../../../../ee/app/assets/javascripts/vue_shared/components/web_ide_link.vue?vue3')),
        LockDirectoryButton: defineAsyncComponent(()=>import('../../../../../ee/app/assets/javascripts/repository/components/lock_directory_button.vue?vue3')),
        FileTreeBrowserToggle
    },
    directives: {
        GlTooltip: GlTooltipDirective
    },
    mixins: [
        InternalEvents.mixin(),
        glListenersMixin
    ],
    inject: [
        'canCollaborate',
        'canEditTree',
        'canPushCode',
        'canPushToBranch',
        'originalBranch',
        'selectedBranch',
        'newBranchPath',
        'newTagPath',
        'newBlobPath',
        'forkNewBlobPath',
        'forkNewDirectoryPath',
        'forkUploadBlobPath',
        'uploadPath',
        'newDirPath',
        'projectRootPath',
        'isReadmeView',
        'isFork',
        'needsToFork',
        'isGitpodEnabledForUser',
        'isBlob',
        'showEditButton',
        'showWebIdeButton',
        'isGitpodEnabledForInstance',
        'showPipelineEditorButton',
        'webIdeUrl',
        'editUrl',
        'pipelineEditorUrl',
        'gitpodUrl',
        'userPreferencesGitpodPath',
        'userProfileEnableGitpodPath',
        'httpUrl',
        'xcodeUrl',
        'sshUrl',
        'kerberosUrl',
        'downloadLinks',
        'isBinary',
        'rootRef',
        'showNoSshKeyMessage',
        'userSettingsSshKeysPath',
        'customCodeDropdownClients'
    ],
    provide () {
        return {
            currentRef: this.currentRef
        };
    },
    props: {
        projectPath: {
            type: String,
            required: true
        },
        refType: {
            type: String,
            required: false,
            default: null
        },
        currentRef: {
            type: String,
            required: false,
            default: null
        },
        projectId: {
            type: String,
            required: true
        }
    },
    computed: {
        ...mapState(useFileTreeBrowserVisibility, [
            'fileTreeBrowserIsVisible',
            'fileTreeBrowserIsExpanded'
        ]),
        isTreeView () {
            return !this.$route.name?.startsWith('blobPath');
        },
        isProjectOverview () {
            return this.$route.name === 'projectRoot';
        },
        isRoot () {
            return !this.currentPath || this.currentPath === '/';
        },
        directoryName () {
            return this.currentPath ? this.currentPath.split('/').pop() : this.projectPath.split('/').pop();
        },
        fileIconName () {
            return this.isTreeView ? 'folder-open' : this.directoryName;
        },
        getRefType () {
            return this.$route.query.ref_type;
        },
        currentPath () {
            return this.$route.params.path;
        },
        refSelectorQueryParams () {
            return {
                sort: 'updated_desc'
            };
        },
        refSelectorValue () {
            return this.refType ? joinPaths('refs', this.refType, this.currentRef) : this.currentRef;
        },
        webIDEUrl () {
            return this.isBlob ? this.webIdeUrl : webIDEUrl(joinPaths('/', this.projectPath, 'edit', this.currentRef, '-', this.$route?.params.path || '', '/'));
        },
        projectIdAsNumber () {
            return getIdFromGraphQLId(this.projectId);
        },
        findFileTooltip () {
            const { description } = START_SEARCH_PROJECT_FILE;
            const key = this.findFileShortcutKey;
            return keyboardShortcutsDisabled() ? null : sanitize(`${description} <kbd class="flat gl-ml-1" aria-hidden=true>${key}</kbd>`);
        },
        findFileShortcutKey () {
            return keysFor(START_SEARCH_PROJECT_FILE)[0];
        },
        showBlobControls () {
            return this.$route.params.path && this.$route.name?.startsWith('blobPath');
        },
        showFileTreeBrowserToggle () {
            return !this.isProjectOverview && !this.fileTreeBrowserIsExpanded;
        },
        toggleFileBrowserShortcutKey () {
            return this.shortcutsEnabled ? keysFor(TOGGLE_FILE_TREE_BROWSER_VISIBILITY)[0] : null;
        },
        shortcutsEnabled () {
            return !keyboardShortcutsDisabled();
        }
    },
    mounted () {
        if (!this.isReadmeView) {
            this.initializeFileTreeBrowser();
            this.bindShortcuts();
        }
    },
    beforeDestroy () {
        this.unbindShortcuts();
    },
    methods: {
        ...mapActions(useFileTreeBrowserVisibility, [
            'initializeFileTreeBrowser',
            'handleFileTreeBrowserToggleClick'
        ]),
        onShortcutToggle () {
            this.handleFileTreeBrowserToggleClick();
            this.trackEvent(this.fileTreeBrowserIsVisible ? EVENT_EXPAND_FILE_TREE_BROWSER_ON_REPOSITORY_PAGE : EVENT_COLLAPSE_FILE_TREE_BROWSER_ON_REPOSITORY_PAGE, {
                label: 'shortcut'
            });
        },
        bindShortcuts () {
            if (this.shortcutsEnabled) {
                Mousetrap.bind(keysFor(TOGGLE_FILE_TREE_BROWSER_VISIBILITY), this.onShortcutToggle);
            }
        },
        unbindShortcuts () {
            if (this.shortcutsEnabled) {
                Mousetrap.unbind(keysFor(TOGGLE_FILE_TREE_BROWSER_VISIBILITY));
            }
        },
        onInput (selectedRef) {
            this.trackEvent(REF_SELECTOR_CLICK);
            visitUrl(generateRefDestinationPath(this.projectRootPath, this.originalBranch, selectedRef));
        },
        handleFindFile () {
            this.trackEvent(FIND_FILE_BUTTON_CLICK);
            Shortcuts.focusSearchFile();
        }
    }
};
